import type { ReturnInterval } from '@/constants/ReturnInterval';
import { Period } from '@/types/period';
import { PeriodAbbrevEnum } from '@/constants/PeriodAbbrevEnum';
import { createDate } from '@/utils/dateUtils';
import type { Ref, ComputedRef } from 'vue';
import { computed } from 'vue';
import { PeriodModule } from '@/store/modules/PeriodStore';
import {
  getCommonlyUsedPeriods,
  getIsPeriodValid,
  getMaxAndLivePeriods,
  getPreviousBusinessDay,
  getPreviousBusinessDayString,
  getUncommonPeriods,
  sortPeriodsByFromDate,
} from '@/utils/period';
import { useItemUnderAnalysisGlobalState } from './useItemUnderAnalysis';
import { useTeamSettingsGlobalState } from './useTeamSettingsGlobalState';
import { useIsPdf } from './usePdf';
import type { DiscoverItem } from '@/types/Discover';
import { DateFormat } from '@/constants/DateFormat';
import { min } from 'lodash';
/**
 * NOTE: this function returns a Map of PeriodAbbrevEnum to Period.
 *
 * If `endDate` is supplied, it will use the given end date.
 * Otherwise, it will use the previous business day.
 *
 * If `disabledBeforeDate` is supplied, it will include the MAX period.
 * If `liveDate` is supplied, it will include the LIVE period.
 *
 * Previously this function did not return the MAX and LIVE periods.
 */
export function useGetAllPeriods({
  endDate,
  disabledBeforeDate,
  liveDate,
}: {
  endDate?: Ref<string | null>;
  disabledBeforeDate?: Ref<string | null>;
  liveDate?: Ref<string | null>;
} = {}): Ref<{ [key in PeriodAbbrevEnum]?: Period }> {
  return computed((): { [key in PeriodAbbrevEnum]?: Period } => {
    const endDateToUse = createDate(endDate?.value ?? getPreviousBusinessDayString());

    const retval = {
      ...getCommonlyUsedPeriods(endDateToUse),
      ...getUncommonPeriods(endDateToUse),
      ...getMaxAndLivePeriods({
        endDate: endDateToUse,
        disabledBeforeDate: disabledBeforeDate?.value,
        liveDate: liveDate?.value,
      }),
    };

    return retval;
  });
}

/**
 * This function returns a computed containing an array of all the available periods for a given track,
 * used in the risk metrics table on the Factsheets.
 *
 * If no `endDate` is provided, it will use the previous business day.
 * If no `disabledBeforeDate` is provided, it will use the global `disabledBeforeDate`.
 * If no `liveDate` is provided, it will use the global `liveDate`.
 *
 * We use the period `toDate` to compute sometimes because, for example, we want the risk metrics table data
 * to be consistent with that of the date picker values chosen by the user.
 *
 * Otherwise, there is an inconsistency between the metrics in the performance summary and the risk metrics table,
 * which in turn might confuse users.
 */
export const useGetAvailablePeriods = ({
  endDate,
  disabledBeforeDate,
  liveDate,
  shouldIncludeMaxPeriod = true,
  shouldIncludeLivePeriod = true,
  shouldOnlyShowPeriodsInPickerDisplayPreferences = true,
}: {
  endDate?: Ref<string | null>;
  disabledBeforeDate?: Ref<string | null>;
  liveDate?: Ref<string | null>;
  shouldIncludeMaxPeriod?: boolean;
  shouldIncludeLivePeriod?: boolean;
  shouldOnlyShowPeriodsInPickerDisplayPreferences?: boolean;
} = {}): Ref<Period[]> => {
  const { disabledBeforeDate: globalDisabledBeforeDate, liveDate: globalLiveDate } = useItemUnderAnalysisGlobalState();

  const liveDateToUse = computed(() => {
    if (!shouldIncludeLivePeriod) return null;

    return liveDate?.value || globalLiveDate.value;
  });

  const endDateToUse = computed(() => endDate?.value ?? getPreviousBusinessDayString());

  const disabledBeforeDateToUse = computed((): string | null => {
    if (!shouldIncludeMaxPeriod) return null;

    return disabledBeforeDate?.value || globalDisabledBeforeDate.value;
  });

  const allPeriodsToEndDate = useGetAllPeriods({
    endDate: endDateToUse,
    disabledBeforeDate: disabledBeforeDateToUse,
    liveDate: liveDateToUse,
  });

  const { periodPickerDisplayPreferences } = useTeamSettingsGlobalState();

  return computed(() => {
    // Ensure allPeriodsToEndDate is always tracked, even when we short-circuit.
    // Without this read, the first run (with null disabledBeforeDate) registers
    // only `disabledBeforeDateToUse` as a dep, and subsequent changes to
    // `liveDate`/`endDate` (which propagate through allPeriodsToEndDate) fail
    // to retrigger this computed.
    const allPeriods = allPeriodsToEndDate.value;

    if (!disabledBeforeDateToUse.value) return [];

    return sortPeriodsByFromDate(
      Object.values(allPeriods).filter((period): boolean => {
        return (
          (!shouldOnlyShowPeriodsInPickerDisplayPreferences ||
            periodPickerDisplayPreferences.value.includes(period.abbrev)) &&
          period.fromDateString() >= (disabledBeforeDateToUse.value as string) &&
          period.fromDateString() < period.toDateString()
        );
      }),
    );
  });
};

export const useGetRiskMetricPeriods = ({
  endDate,
  disabledBeforeDate,
}: {
  endDate?: Ref<string>;
  disabledBeforeDate?: Ref<string>;
} = {}): ComputedRef<Period[]> => {
  const isPdf = useIsPdf();

  const allPeriodsToEndDate = useGetAvailablePeriods({
    endDate,
    disabledBeforeDate,
    // on PDF, we want to ONLY show the periods that are in the picker display preferences
    // otherwise we show all the default periods, PLUS the periods in the picker display preferences
    shouldOnlyShowPeriodsInPickerDisplayPreferences: isPdf.value,
  });

  const { periodPickerDisplayPreferences } = useTeamSettingsGlobalState();

  const periodsToInclude = computed(
    () =>
      new Set([
        PeriodAbbrevEnum.SIX_M,
        PeriodAbbrevEnum.ONE_Y,
        PeriodAbbrevEnum.THREE_Y,
        PeriodAbbrevEnum.FIVE_Y,
        PeriodAbbrevEnum.TEN_Y,
        PeriodAbbrevEnum.LIVE,
        ...periodPickerDisplayPreferences.value,
      ]),
  );

  return computed((): Period[] => {
    return allPeriodsToEndDate.value.filter((p) => {
      return periodsToInclude.value.has(p.abbrev);
    });
  });
};

/**
 * Sets the period, custom period dates, and return interval from URL params.
 * This should be called BEFORE usePeriod() to ensure all components get the correct period.
 *
 * @param urlParams - URL params containing period, returnInterval, and optionally startDate/endDate
 */
export function setPeriodFromUrlParams(urlParams: {
  period: PeriodAbbrevEnum;
  returnInterval: ReturnInterval;
  startDate?: string;
  endDate?: string;
}): void {
  if (!urlParams.period || !urlParams.returnInterval) {
    console.warn('setPeriodFromUrlParams: Missing required param (period or returnInterval)');
    return;
  }

  if (
    [PeriodAbbrevEnum.CUST, PeriodAbbrevEnum.MAX].includes(urlParams.period) &&
    urlParams.startDate &&
    urlParams.endDate
  ) {
    PeriodModule.SetCustomPeriod({
      fromDate: createDate(urlParams.startDate),
      toDate: createDate(urlParams.endDate),
    });
  } else {
    PeriodModule.SetPeriod(urlParams.period);
  }

  PeriodModule.SetReturnInterval(urlParams.returnInterval);
}

export function useGetMinHistoryDate({ items }: { items: Ref<DiscoverItem[]> }): ComputedRef<string> {
  return computed(() => {
    const dates = items.value.map((item) => item.historyStartDate);
    return min(dates) || createDate().toFormat(DateFormat.YYYY_MM_DD);
  });
}

/**
 * Composable function to handle periods.
 * Supports both global periods (via PeriodModule) and local periods (via `localPeriod`).
 *
 * Uses local period if provided, otherwise falls back to the global period
 */
export default function ({
  localPeriod,
  localReturnInterval,
  localDisabledBeforeDate,
  localDisabledAfterDate,
}: {
  localPeriod?: Ref<Period | null>;
  localReturnInterval?: Ref<ReturnInterval | null>;
  localDisabledBeforeDate?: Ref<string | null>;
  localDisabledAfterDate?: Ref<string | null>;
} = {}): {
  period: Ref<Period>;
  globalPeriod: Ref<Period>;
  defaultValidPeriodStartDate: Ref<string>;
  returnInterval: Ref<ReturnInterval>;
  globalReturnInterval: Ref<ReturnInterval>;
  disabledBeforeDate: Ref<string>;
  disabledAfterDate: Ref<string>;
  isPeriodValid: Ref<boolean>;
  rollingPeriod: Ref<Period | null>;
} {
  const globalPeriod = computed(() => PeriodModule.currentPeriod);
  const period = computed({
    get: () => {
      if (localPeriod && localPeriod.value) {
        return localPeriod.value;
      }

      return globalPeriod.value;
    },
    set: (newVal) => {
      if (localPeriod) {
        localPeriod.value = newVal;
        return;
      }

      if (newVal.abbrev === PeriodAbbrevEnum.CUST) {
        PeriodModule.SetCustomPeriod(newVal);
        return;
      }

      PeriodModule.SetPeriod(newVal);
    },
  });

  const globalReturnInterval = computed(() => PeriodModule.currentReturnInterval);
  const defaultValidPeriodStartDate = computed((): string => PeriodModule.defaultValidPeriodStartDate);
  const returnInterval = computed({
    get(): ReturnInterval {
      if (localReturnInterval && localReturnInterval.value) {
        return localReturnInterval.value;
      }

      return globalReturnInterval.value;
    },
    set(newVal: ReturnInterval) {
      if (localReturnInterval) {
        localReturnInterval.value = newVal;
        return;
      }

      PeriodModule.SetReturnInterval(newVal);
    },
  });
  const previousBusinessDay = getPreviousBusinessDay();

  const globalDisabledBeforeDate = computed((): string => PeriodModule.disabledBeforeDate);
  const disabledBeforeDate = computed((): string => localDisabledBeforeDate?.value || globalDisabledBeforeDate.value);

  const globalDisabledAfterDate = computed((): string => PeriodModule.disabledAfterDate);
  const disabledAfterDate = computed((): string => localDisabledAfterDate?.value ?? globalDisabledAfterDate.value);

  const isPeriodValid = computed((): boolean =>
    getIsPeriodValid({
      period: period.value,
      disabledBeforeDate: disabledBeforeDate.value,
      disabledAfterDate: disabledAfterDate.value,
    }),
  );

  /**
   * We use slightly different logic for rolling graphs. Instead of using the current period, we use the entire track
   * history up until the start of the current period.
   */
  const rollingPeriod = computed(() => {
    if (!disabledBeforeDate.value) return null;

    return new Period({
      fromDate: createDate(disabledBeforeDate.value),
      toDate: previousBusinessDay,
      abbrev: PeriodAbbrevEnum.CUST,
    });
  });

  return {
    period,
    globalPeriod,
    defaultValidPeriodStartDate,
    returnInterval,
    globalReturnInterval,
    disabledBeforeDate,
    disabledAfterDate,
    isPeriodValid,
    rollingPeriod,
  };
}
